import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { confirmRelationshipSuggestion } from 'store/graph/actions';
import { selectSuggestedEdgeByKey } from 'store/graph/selectors';
import RelationshipTypeSelector from './relationship-type-selector';
import PersonCard from '../suggested-edge-detail/person-card';
import './style.css';

const ConfirmRelationshipModal = ({ edgeKey, onClose }) => {
  const dispatch = useDispatch();
  const edge = useSelector(selectSuggestedEdgeByKey(edgeKey));
  const [relationshipType, setRelationshipType] = useState('Personal');
  const modalRef = useRef();
  const closeButtonRef = useRef();
  const stopEvent = (event) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (edge) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKeyPress = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      };
      
      const handleEscapeKeyPress = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      modalRef.current.addEventListener('keydown', handleTabKeyPress);
      document.addEventListener('keydown', handleEscapeKeyPress);
      
      // Focus the close button on open
      closeButtonRef.current?.focus();

      return () => {
        modalRef.current?.removeEventListener('keydown', handleTabKeyPress);
        document.removeEventListener('keydown', handleEscapeKeyPress);
      };
    }
  }, [edge, onClose]);

  if (!edge) {
    return null;
  }

  const edgeSignals = Array.isArray(edge.signals) ? edge.signals : [];

  const handleConfirm = () => {
    // Keep action shape compatible with existing tests and reducers by
    // not passing a details object (it defaults to null in the action creator).
    dispatch(
      confirmRelationshipSuggestion(
        edge.id1,
        edge.id2,
        relationshipType,
        edgeSignals,
      ),
    );
    onClose();
  };

  return (
    <div className="modal-backdrop" onMouseDown={stopEvent} onClick={stopEvent}>
      <div
        className="confirm-relationship-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-header"
      >
        <div className="modal-header">
          <h2 id="confirm-modal-header">Confirm Relationship</h2>
          <button type="button" onClick={onClose} className="close-btn" ref={closeButtonRef} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body">
          <div className="person-cards-container-modal">
            <PersonCard personId={edge.id1} />
            <span className="connector-arrow">↔</span>
            <PersonCard personId={edge.id2} />
          </div>
          
          <RelationshipTypeSelector
            selectedType={relationshipType}
            onTypeChange={setRelationshipType}
          />

          <div className="evidence-section">
            <h4>Based on the following signals:</h4>
            <div className="signal-pills-modal">
              {edgeSignals.map((signal) => (
                <span key={signal} className={`pill-modal ${signal}`}>
                  {signal}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="create-btn" onClick={handleConfirm}>Create Relationship</button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmRelationshipModal;
